import { FC, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Skeleton } from 'src/components/Skeleton'
import {
  AI_LISTENING_BRAND_ROUTE,
  AI_LISTENING_COMPETITORS_ROUTE,
  AI_LISTENING_COMPETITOR_ROUTE,
} from 'src/constants/routes'
import { useAiListeningLink } from 'src/hooks/useAiListeningDateRange'
import { useCreateAiListeningCompetitorMutation } from 'src/reactQuery/useCreateAiListeningCompetitorMutation'
import { useGetAiListeningProfileQuery } from 'src/reactQuery/useGetAiListeningProfileQuery'
import { AiListeningPage, PageBreadcrumb } from '../shared/Layout'
import { ProfileForm, ProfileFormValues } from '../shared/ProfileForm'

// /ai-listening/brand/:profileId/competitors/new
export const CreateCompetitorPage: FC = () => {
  const { profileId = '' } = useParams<{ profileId: string }>()
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const [submitError, setSubmitError] = useState<string>()

  const {
    data: profile,
    isError,
    isLoading,
  } = useGetAiListeningProfileQuery(profileId)

  const competitorsLink = link(AI_LISTENING_COMPETITORS_ROUTE, { profileId })

  const createMutation = useCreateAiListeningCompetitorMutation({
    onError: (error) =>
      setSubmitError(
        error.response?.data?.error ||
          "We couldn't add the competitor. Please try again."
      ),
    onSuccess: (competitor) => {
      toast.success(`${competitor.brandName} added to your competitors`)
      navigate(
        link(AI_LISTENING_COMPETITOR_ROUTE, {
          competitorId: competitor._id,
          profileId,
        })
      )
    },
  })

  const handleSubmit = (values: ProfileFormValues) => {
    setSubmitError(undefined)
    createMutation.mutate({
      payload: {
        brandName: values.brandName,
        brandNames: values.aliases,
        name: values.name,
        website: values.website || undefined,
      },
      profileId,
    })
  }

  if (isLoading) {
    return (
      <AiListeningPage>
        <Skeleton height={20} width={260} />
        <Skeleton height={720} radius={20} width="100%" />
      </AiListeningPage>
    )
  }

  if (isError || !profile) {
    return <Navigate replace to={link(AI_LISTENING_BRAND_ROUTE)} />
  }

  return (
    <AiListeningPage>
      <PageBreadcrumb
        backTo={competitorsLink}
        items={['AI Listening', 'Add Competitor']}
      />
      <ProfileForm
        isSubmitting={createMutation.isPending}
        onCancel={() => navigate(competitorsLink)}
        onSubmit={handleSubmit}
        readOnlyAiTools={profile.aiTools}
        submitError={submitError}
        subtitle="Add the basic information about the brand you want to track"
        title="Create Your Competitor’s Profile"
        variant="competitor"
      />
    </AiListeningPage>
  )
}
